import { AppShell, Burger, Group, NavLink, ScrollArea, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet, NavLink as RouterNavLink, useLocation } from 'react-router';
import logoWhite from '../assets/deco-stop-logo-white.svg';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useUnitNames } from '../hooks/useUnitNames';
import { type PortalLocale, portalLocale } from '../lib/locale';
import { brand } from '../theme/brand';
import { appLayoutCopy } from './appLayoutCopy';
import { UserMenu } from './UserMenu';

/**
 * Grouped the way the owner thinks about the business, not the way the API is partitioned.
 *
 * Staff and pay were previously two sections apart — someone's details under "Operations" and
 * what they are paid under "Back Office" — which is the structural form of the complaint that
 * started this work. Anything about a person now sits together.
 */
const NAV = [
  { to: '/', label: 'dashboard' },
  { group: 'menuGroup' },
  { to: '/catalog', label: 'menuItems' },
  { to: '/pricing', label: 'pricing' },
  { to: '/tax', label: 'tax' },
  { group: 'peopleGroup' },
  { to: '/staff', label: 'staff' },
  { to: '/payroll', label: 'payroll' },
  { to: '/customers', label: 'customers' },
  { group: 'serviceGroup' },
  { to: '/floor', label: 'floor' },
  { to: '/devices', label: 'devices' },
  { to: '/register', label: 'register' },
  { to: '/fiscal', label: 'fiscal' },
  { to: '/channels', label: 'channels' },
  { to: '/referrals', label: 'referrals' },
  { group: 'stockGroup' },
  { to: '/inventory', label: 'inventory' },
  { to: '/suppliers', label: 'suppliers' },
  { group: 'recordsGroup' },
  // The bar's money — sales, how people paid, and what is left after the food. The OWNER's, and
  // the only row here that is: `ownerOnly` is why every other line needs no flag.
  { to: '/reports', label: 'reports', ownerOnly: true },
  { to: '/filed-months', label: 'filedMonths' },
  { to: '/audit', label: 'audit' },
  { to: '/settings', label: 'settings' },
  { to: '/mcp', label: 'mcp' },
] as const;

export function AppLayout({ locale = portalLocale() }: { locale?: PortalLocale }) {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const text = appLayoutCopy(locale);
  // What more than one of each counting unit is called, fetched once for every screen below —
  // `hooks/useUnitNames.ts` says why it is here rather than at each call site.
  useUnitNames();
  /*
   * A row somebody cannot open is ABSENT, not disabled. A grayed-out entry is a promise the
   * software has already decided not to keep, and on an admin site shared with a manager it is
   * also a list of what somebody else is trusted with. The register's menu already works this way
   * (`Surfaces.Reachable`), and this is the same rule on the other screen.
   *
   * It is not the gate. `/admin/v1/reports/*` refuses a non-owner at the mount whatever this
   * component draws — hiding a link has never been how this system decides who may read what.
   */
  const { isOwner } = useCurrentUser();
  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      {/* Deep-teal band, matching the site's dark sections and the sign-in gradient. */}
      <AppShell.Header
        style={{
          backgroundColor: brand.deep,
          borderBottom: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <Group h="100%" px="md" gap={12} wrap="nowrap">
          <Burger
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
            color={brand.sand}
            aria-label={text.openNavigation}
          />
          {/* deco-stop-logo-white.svg is the same crest recoloured to the brand off-white, so
              it sits directly on the teal — no white chip needed. 0.916 w/h, never forced
              square; 40px is the floor at which the brush wordmark stays legible. */}
          <img src={logoWhite} alt="" style={{ height: 40, width: 'auto', display: 'block' }} />
          <div style={{ lineHeight: 1 }}>
            <div
              style={{
                fontFamily: brand.font,
                fontSize: 19,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: brand.sand,
              }}
            >
              Deco Stop
            </div>
            <div
              style={{
                fontFamily: brand.font,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: brand.gold,
                marginTop: 2,
              }}
            >
              {text.operations}
            </div>
          </div>
          <Group ml="auto">
            <UserMenu locale={locale} />
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="xs">
        <ScrollArea>
          {NAV.filter((item) => !('ownerOnly' in item && item.ownerOnly) || isOwner).map((item) =>
            'group' in item ? (
              <Text key={item.group} size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm" mb={4} px="xs">
                {text.navigation[item.group]}
              </Text>
            ) : (
              <NavLink
                key={item.to}
                component={RouterNavLink}
                to={item.to}
                label={text.navigation[item.label]}
                // Prefix match, so opening one person keeps "Staff" highlighted instead of
                // leaving the sidebar looking like you have navigated out of the section.
                active={item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)}
                onClick={() => {
                  // The drawer covers the page on a phone; leaving it open after a tap hides the
                  // screen the user just asked for.
                  if (opened) toggle();
                }}
              />
            ),
          )}
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
